export const focusableSelector = `button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])`;
export const priorityFocusableSelector = 'input, textarea';
const active = () => document === null || document === void 0 ? void 0 : document.activeElement;
export const getScope = (shards) => {
    if (!shards)
        return [];
    return toArray(shards)
        .map((el) => { var _a; return (_a = el.current) !== null && _a !== void 0 ? _a : el; })
        .filter((el) => el instanceof HTMLElement);
};
const queryOnElement = (el, selector) => {
    let result = [];
    if (el.matches(selector))
        result.push(el); // Return self if matches
    const nodeList = el.querySelectorAll(selector);
    if (nodeList.length > 0)
        result = result.concat(Array.from(nodeList));
    return result.length > 0 ? result : null;
};
export const queryOnScope = (scope, selector) => {
    let targetElements = [];
    scope instanceof Array
        ? (targetElements = scope)
        : (targetElements = [scope]);
    if (targetElements.length === 0)
        return null;
    targetElements = targetElements
        .map((el) => queryOnElement(el, selector))
        .flat()
        .filter((el) => el);
    if (targetElements.length === 0)
        return null;
    return targetElements;
};
export const getFocusables = (scope, selector = focusableSelector // Crucial thing
) => {
    const focusables = queryOnScope(scope, selector);
    if (!focusables || focusables.length === 0)
        return { focusables: [], first: null, last: null };
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    return { focusables, first, last };
};
export const isInside = (scope, target) => {
    const scopeElement = scope.find((item) => item === null || item === void 0 ? void 0 : item.contains(target));
    if (!scopeElement)
        return false;
    // Check if target element is inside another FocusTrap scope
    if (isInsideNestedTrap(scopeElement, target))
        return false;
    return true;
};
const isInsideNestedTrap = (scopeElement, target) => {
    let current = target;
    let found = false;
    // Check if some parent of active element has nested useFocusTrap marker
    while (scopeElement.contains(current) && scopeElement !== current) {
        if (current.hasAttribute('data-focus-locked')) {
            found = true; // Nested scope found
            break;
        }
        current = current.parentElement;
    }
    return found;
};
export const tabIndexInside = (scope) => {
    return getScope(scope).some((el) => getFocusables(el).focusables.some((e) => getTabIndex(e) > 0));
};
export const shardsUtils = (initialScope) => {
    let currentShardIdx = 0;
    const scope = toArray(initialScope);
    scope.forEach((el, i) => {
        if (el === null || el === void 0 ? void 0 : el.contains(active()))
            currentShardIdx = i;
    });
    const nextShardIdx = nextIdx(scope, currentShardIdx);
    const prevShardIdx = prevIdx(scope, currentShardIdx);
    const current = getFocusables(scope[currentShardIdx]);
    const next = getFocusables(scope[nextShardIdx]);
    const prev = getFocusables(scope[prevShardIdx]);
    return {
        firstCurrentShard: current === null || current === void 0 ? void 0 : current.first,
        lastCurrentShard: current === null || current === void 0 ? void 0 : current.last,
        firstNextShard: next.first,
        lastPrevShard: prev.last,
    };
};
export const focusNext = (scope, event) => {
    const { first, last } = getFocusables(scope);
    const shards = shardsUtils(scope);
    if (active() === last) {
        event === null || event === void 0 ? void 0 : event.preventDefault(); // Prevent focus on focusable outside of useFocusLock
        first.focus();
        return;
    }
    if (active() === shards.lastCurrentShard) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        shards.firstNextShard.focus();
    }
};
export const focusPrev = (scope, event) => {
    const { first, last } = getFocusables(scope);
    const shards = shardsUtils(scope);
    if (active() === first) {
        event === null || event === void 0 ? void 0 : event.preventDefault(); // Prevent focusing on focusable outside of useFocusLock scope
        last.focus();
        return;
    }
    if (active() === shards.firstCurrentShard) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        shards.lastPrevShard.focus();
    }
};
const toArray = (obj) => (obj instanceof Array ? obj : [obj]);
const prevIdx = (array, current) => {
    if (current < 0)
        return current;
    return current === 0 ? array.length - 1 : current - 1;
};
const nextIdx = (array, current) => {
    if (current < 0)
        return current;
    return current === array.length - 1 ? 0 : current + 1;
};
const getTabIndex = (el) => {
    const idx = parseInt(el.getAttribute('tabIndex'), 10);
    if (!idx)
        return 0;
    return idx;
};
// Focus on prev focusable if focusable was removed
const focusPrevOnUnmount = (el, scope) => {
    const { focusables: f1 } = getFocusables(scope);
    const idx = f1.indexOf(el);
    const length1 = f1.length;
    setTimeout(() => {
        const { focusables: f2 } = getFocusables(scope);
        if (document === null || document === void 0 ? void 0 : document.body.contains(el))
            return;
        if (f2.length === 0)
            return;
        if (length1 !== f2.length + 1)
            return;
        idx === 0 ? f2[f2.length - 1].focus() : f2[idx - 1].focus();
    }, 10);
};
export const handleFocusablesUnmount = (scope) => {
    const once = { once: true };
    getFocusables(scope).focusables.forEach((el) => {
        const handleBlur = () => focusPrevOnUnmount(el, scope);
        el.removeEventListener('blur', handleBlur);
        el.addEventListener('blur', handleBlur, once);
    });
};
export const setFocusLockMarkers = (scope) => {
    scope.forEach((el) => el.setAttribute('data-focus-locked', ''));
};
export const cleanFocusLockMarkers = (scope) => {
    scope.forEach((el) => el.removeAttribute('data-focus-locked'));
};
//# sourceMappingURL=utils.js.map